import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { webhookService } from '@/services/webhookService';

// Define types locally since they might not exist in @/types/api
interface Webhook {
  id: string;
  url: string;
  event_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  // Add other webhook properties as needed
}

interface WebhookFilters {
  page: number;
  limit: number;
  status: string;
  event_type: string;
  startDate: string;
  endDate: string;
  search: string;
}

interface WebhookListResponse {
  data: Webhook[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

// Webhook state interface
interface WebhookState {
  webhooks: Webhook[];
  currentWebhook: Webhook | null;
  loading: boolean;
  error: string | null;
  filters: WebhookFilters;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// Initial state
const initialState: WebhookState = {
  webhooks: [],
  currentWebhook: null,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    status: '',
    event_type: '',
    startDate: '',
    endDate: '',
    search: '',
  },
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  },
};

// Async thunks
export const fetchWebhooks = createAsyncThunk(
  'webhooks/fetchWebhooks',
  async (filters: WebhookFilters, { rejectWithValue }) => {
    try {
      const response = await webhookService.getWebhookTransactions(filters.page, filters.limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch webhooks');
    }
  }
);

export const fetchWebhookById = createAsyncThunk(
  'webhooks/fetchWebhookById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await webhookService.getWebhookById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch webhook');
    }
  }
);

export const createWebhook = createAsyncThunk(
  'webhooks/createWebhook',
  async (webhookData: Partial<Webhook>, { rejectWithValue }) => {
    try {
      // Mock implementation - replace with actual API call
      const response = { id: '1', ...webhookData };
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create webhook');
    }
  }
);

export const updateWebhook = createAsyncThunk(
  'webhooks/updateWebhook',
  async ({ id, data }: { id: string; data: Partial<Webhook> }, { rejectWithValue }) => {
    try {
      // Mock implementation - replace with actual API call
      const response = { id, ...data };
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update webhook');
    }
  }
);

export const deleteWebhook = createAsyncThunk(
  'webhooks/deleteWebhook',
  async (id: string, { rejectWithValue }) => {
    try {
      // Mock implementation - replace with actual API call
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete webhook');
    }
  }
);

export const testWebhook = createAsyncThunk(
  'webhooks/testWebhook',
  async (id: string, { rejectWithValue }) => {
    try {
      // Mock implementation - replace with actual API call
      const response = { success: true };
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to test webhook');
    }
  }
);

// Webhook slice
const webhooksSlice = createSlice({
  name: 'webhooks',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<WebhookFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        status: '',
        event_type: '',
        startDate: '',
        endDate: '',
        search: '',
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentWebhook: (state) => {
      state.currentWebhook = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Webhooks
      .addCase(fetchWebhooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWebhooks.fulfilled, (state, action: PayloadAction<WebhookListResponse>) => {
        state.loading = false;
        state.webhooks = action.payload.data;
        state.pagination = {
          currentPage: action.payload.current_page,
          totalPages: action.payload.last_page,
          totalItems: action.payload.total,
          itemsPerPage: action.payload.per_page,
        };
        state.error = null;
      })
      .addCase(fetchWebhooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Webhook by ID
      .addCase(fetchWebhookById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWebhookById.fulfilled, (state, action: PayloadAction<Webhook>) => {
        state.loading = false;
        state.currentWebhook = action.payload;
        state.error = null;
      })
      .addCase(fetchWebhookById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create Webhook
      .addCase(createWebhook.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWebhook.fulfilled, (state, action: PayloadAction<Webhook>) => {
        state.loading = false;
        state.webhooks.unshift(action.payload);
        state.error = null;
      })
      .addCase(createWebhook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update Webhook
      .addCase(updateWebhook.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWebhook.fulfilled, (state, action: PayloadAction<Webhook>) => {
        state.loading = false;
        const index = state.webhooks.findIndex(w => w.id === action.payload.id);
        if (index !== -1) {
          state.webhooks[index] = action.payload;
        }
        if (state.currentWebhook?.id === action.payload.id) {
          state.currentWebhook = action.payload;
        }
        state.error = null;
      })
      .addCase(updateWebhook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete Webhook
      .addCase(deleteWebhook.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWebhook.fulfilled, (state, action) => {
        state.loading = false;
        state.webhooks = state.webhooks.filter(w => w.id !== action.payload);
        if (state.currentWebhook?.id === action.payload) {
          state.currentWebhook = null;
        }
        state.error = null;
      })
      .addCase(deleteWebhook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Test Webhook
      .addCase(testWebhook.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testWebhook.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(testWebhook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, clearError, clearCurrentWebhook } = webhooksSlice.actions;
export default webhooksSlice;
